import dayjs, { Dayjs } from "dayjs"

import {
  CLASS_GROUP_LABELS,
  CLASS_GROUPS,
  ROOM_LABELS,
  type ROOMS,
} from "#constants/classOptions"
import {
  type ClassConstructor,
  type ClassForTable,
} from "#customTypes/composedTypes/classTypes"
import { type CurriculumConstructor } from "#customTypes/composedTypes/curriculumTypes"
import { UserConstructor } from "#customTypes/composedTypes/userTypes"
import { type Uuid } from "#customTypes/globalTypes"
import { STANDARD_DAY_FORMAT } from "#utils/dateHelpers"
import { formatSemester } from "#utils/semesterHelpers"
import { stringify } from "#utils/textFormatters"

export class Class {
  id: Uuid
  name: string
  group: CLASS_GROUPS
  cycle: string
  defaultTeacherId: Uuid

  sessionIds: Uuid[]
  notes: string
  curriculumId: Uuid
  isSummerCamp: boolean
  isMakeupAllowed: boolean
  isTryoutAllowed: boolean
  semester: string
  capacity: string
  room: ROOMS | undefined
  waitlist: string
  startDate: Dayjs | null
  startTime: string
  endTime: string
  zoomLink: string

  studentIds: Uuid[]
  teacherIds: Uuid[]
  assistantIds: Uuid[]

  constructor(props: ClassConstructor) {
    this.id = props.id
    this.name = props.name ?? ""
    this.group = props.group ?? ""
    this.curriculumId = props.curriculumId
    this.cycle = props.cycle ?? ""
    this.defaultTeacherId = props.defaultTeacherId ?? ""

    this.sessionIds = props.sessionIds ?? []

    this.notes = props.notes ?? ""
    this.capacity = props.capacity ?? ""
    this.room = props.room
    this.waitlist = props.waitlist ?? ""
    this.semester = props.semester ?? ""
    this.startDate = props.startDate != null ? dayjs(props.startDate) : null
    this.startTime = props.startTime ?? ""
    this.endTime = props.endTime ?? ""
    this.zoomLink = props.zoomLink ?? ""

    this.isSummerCamp = props.isSummerCamp ?? false
    this.isMakeupAllowed = props.isMakeupAllowed ?? true
    this.isTryoutAllowed = props.isTryoutAllowed ?? true

    // Aggregated
    this.studentIds = props.studentIds ?? []
    this.teacherIds = props.teacherIds ?? []
    this.assistantIds = props.assistantIds ?? []
  }

  getDisplayName = () => {
    const semesterLabel = `${formatSemester(this.semester)}${this.isSummerCamp ? " Camp" : ""}`

    return `${semesterLabel} - ${this.startDate?.format(STANDARD_DAY_FORMAT)} ${this.startTime} to ${this.endTime} - ${CLASS_GROUP_LABELS[this.group]}`
  }

  // Show ⚠️ for missing data, will re-render when it arrives
  getClassForTable = (
    curriculum: CurriculumConstructor | null | undefined,
    teachers: UserConstructor[] = []
  ): ClassForTable => {
    try {
      const teacherString =
        teachers
          ?.filter(Boolean)
          ?.map((teacher) => teacher.name)
          ?.join(", ") ?? "⚠️"

      return {
        id: this.id,
        name: this.name,
        curriculum: curriculum?.name ?? "⚠️",
        weekday: this.startDate?.format(STANDARD_DAY_FORMAT) ?? "",
        startTime: this.startTime,
        endTime: this.endTime,
        room: this.room ? ROOM_LABELS[this.room] : "",
        teachers: teacherString,
        skillLevels: curriculum?.skillLevels?.join(", ") ?? "⚠️",
        headcount: `${this.studentIds.length}/${this.capacity}`,
        notes: this.notes,
        semester: this.semester,
        makeups: this.isMakeupAllowed ? "✓" : "❌",
        tryouts: this.isTryoutAllowed ? "✓" : "❌",
        summerCamp: this.isSummerCamp ? "✓" : "❌",
      }
    } catch (error) {
      console.log(`error: ${this.id}: teachers: ${stringify(teachers)}`)
      console.error(error)
      // Return partial data on error to prevent table from breaking
      return {
        id: this.id,
        name: this.name,
        curriculum: "⚠️",
        weekday: this.startDate?.format(STANDARD_DAY_FORMAT) ?? "",
        startTime: this.startTime,
        endTime: this.endTime,
        room: this.room ? ROOM_LABELS[this.room] : "",
        teachers: "⚠️",
        skillLevels: "⚠️",
        headcount:
          this.studentIds && this.capacity
            ? `${this.studentIds.length}/${this.capacity}`
            : "⚠️",
        notes: this.notes,
        semester: this.semester,
        makeups: this.isMakeupAllowed ? "✓" : "❌",
        tryouts: this.isTryoutAllowed ? "✓" : "❌",
        summerCamp: this.isSummerCamp ? "✓" : "❌",
      }
    }
  }

  //   toConstructor = (): ClassConstructor => ({
  //     id: this.id,
  //     name: this.name,
  //     group: this.group,
  //     cycle: this.cycle,
  //     period: this.period,
  //     sessionIds: this.sessionIds,
  //     notes: this.notes,
  //     isSummerCamp: this.isSummerCamp,
  //     curriculumId: this.curriculumId,
  //     isMakeupAllowed: this.isMakeupAllowed,
  //     isTryoutAllowed: this.isTryoutAllowed,
  //     capacity: this.capacity,
  //     room: this.room,
  //     waitlist: this.waitlist,
  //     startDate: this.startDate?.toISOString() ?? "",
  //     startTime: this.startTime,
  //     endTime: this.endTime,
  //     zoomLink: this.zoomLink,
  //   })
}
